import * as React from 'react';
import { Fragment, useCallback } from 'react';
import {
    Count,
    DatagridConfigurable,
    DateField,
    EditButton,
    ExportButton,
    Filter,
    FilterButton,
    List,
    SearchInput,
    SelectColumnsButton,
    SelectInput,
    ShowButton,
    TextField,
    TextInput,
    TopToolbar,
    useGetIdentity,
    useGetList,
    useListContext,
} from 'react-admin';
import { Divider, Tabs, Tab, Typography, Select, MenuItem } from '@mui/material';
import { AccountCircle, FileUpload, Image, Videocam } from '@mui/icons-material';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { Roles } from '../../common/constants';
import FullNameField from '../../common/FullNameField';
import useIsPermission from '../../hooks/useIsPermission';
import ClearFiltersOnRouteChange from '../../common/ClearFiltersOnRouteChange';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';
import CommonModal from '../../common/ui/CommonModal';
import BulkUploadCreate from '../bulk-uploads/BulkUploadCreate';

const ListActions = () => {
    const { isWritable } = useIsPermission('USERS');
    const navigate = useNavigate();
    const [bulkUploadModal, setBulkUploadModal] = React.useState(false);

    return (
        <>
            <TopToolbar>
                <div className="flex text-sm font-semibold   gap-2 justify-start text-[#4f3cc9]  items-center cursor-pointer hover:bg-blue-50">
                    <button
                        onClick={() => setBulkUploadModal(true)}
                        className="flex items-center uppercase gap-2"
                    >
                        <FileUpload fontSize="small" /> Bulk Upload
                    </button>
                </div>
                {isWritable && (
                    <>
                        <Button
                            startIcon={<AccountCircle />}
                            onClick={() => {
                                navigate(`/users/create`);
                            }}
                            sx={{ padding: '0 5px' }}
                        >
                            Add User
                        </Button>
                        <SelectColumnsButton />
                    </>
                )}
            </TopToolbar>
            <CommonModal
                title="Student Bulk Upload"
                open={bulkUploadModal}
                setOpen={setBulkUploadModal}
            >
                <BulkUploadCreate selectedType="STUDENT" />
            </CommonModal>
        </>
    );
};

const predefinedRoles = [
    { name: 'STUDENT', id: 'STUDENT' },
    { name: 'ORG_ADMIN', id: 'ORG_ADMIN' },
];

const UserFilter = (props: any) => {
    const { data: rolesList = [] } = useGetList<any>('roles', {
        sort: { field: 'createdAt', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
    });

    const roleOptions = [
        ...predefinedRoles,
        ...rolesList.map(role => ({
            name: role.name,
            id: role.name,
        })),
    ];

    return (
        <Filter {...props}>
            <TextInput source="phone" label="search by phone" alwaysOn />
            <TextInput source="name" label="search by first name" alwaysOn />
            <SelectInput source="role" label="Role" choices={roleOptions} alwaysOn />
        </Filter>
    );
};

const UserList = () => {
    const { data } = useGetIdentity();
    const { isDeletable, isUpdatable } = useIsPermission('USERS');

    if (data?.role === Roles.ADMIN) {
        return (
            <div>
                <Typography>You can see Users after logging in to organization account</Typography>
            </div>
        );
    }
    return (
        <List
            filterDefaultValues={{ role: '' }}
            sort={{ field: 'createdAt', order: 'DESC' }}
            perPage={25}
            filters={<UserFilter />}
            actions={<ListActions />}
            queryOptions={{
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            }}
        >
            <ClearFiltersOnRouteChange />
            <DatagridConfigurable omit={['value']}>
                <FullNameField />
                <TextField source="countryCode" sortable={false} />
                <TextField source="phone" sortable={false} />
                <TextField source="email" sortable={false} />
                <TextField source="role" sortable={false} />
                <TextField source="status" sortable={false} />
                <FormattedDateTimeField source="createdAt" sortable={false} />
                {isUpdatable && <EditButton />}
                {isDeletable && <ConfirmDeleteButton />}
                <ShowButton />
            </DatagridConfigurable>
        </List>
    );
};

export default UserList;
